import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Container,
  BackButton,
  Title,
  Section,
  PodiumGrid,
  PodiumCard,
  PodiumName,
  LeaderboardGrid,
  LeaderboardItem,
  LeaderboardRank,
  LeaderboardName,
  EmptyStateCard,
  EmptyStateTitle,
  EmptyStateText,
  PodiumColumn,
  PodiumIcon,
  LoadingContainer,
  EmptyStateIcon,
  PodiumContainer,
  PodiumUsernameWrapper,
  TemplateRow,
  TemplateColumn,
  TemplateValue,
  TemplateLabel,
  FooterText,
  TemplateFooter,
  Pagination,
  PaginationButton,
  PaginationCurrent,
  TemplatesSection,
  MobileTemplateRow,
} from "./RewardsTemplatePage.styles";

import MainContainer from "../../components/MainContainer";
import { useNavigate } from "react-router-dom";

import { ReactComponent as StarSvg } from "../../assets/icons/star.svg";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as TrophySvg } from "../../assets/icons/rewards.svg";

import FirstPng from "../../assets/images/1.png";
import SecondPng from "../../assets/images/2.png";
import ThirdPng from "../../assets/images/3.png";

import Button from "../../components/utils/Button/Button";
import {
  selectGetTopCreatorsPending,
  selectTopCreators,
} from "../../store/slices/model/slice";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { fetchTopCreators } from "../../store/slices/model/asyncThunks";
import { selectUser } from "../../store/slices/user/slice";

import { formatTimeAgo, priceFormatter } from "../../utils/utils";

const RewardsTemplatePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectLoggedIn);
  const user = useSelector(selectUser);

  const topCreators = useSelector(selectTopCreators);
  const loading = useSelector(selectGetTopCreatorsPending);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchTopCreators())
        .unwrap()
        .then()
        .catch((err) => {
          console.log(err);
        });
    }
  }, [dispatch, isLoggedIn]);

  const handleBack = () => {
    navigate("/");
    localStorage.setItem("activeTabIndex", "0");
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const podiumOrder = [2, 1, 3];

  const getPaginatedTemplates = () => {
    if (!user?.approvedTrainModels) return [];

    const templatesArray = Object.entries(user.approvedTrainModels);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return templatesArray.slice(startIndex, endIndex);
  };

  const totalTemplates = user?.approvedTrainModels
    ? Object.keys(user.approvedTrainModels).length
    : 0;
  const totalPages = Math.ceil(totalTemplates / itemsPerPage);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const paginatedTemplates = getPaginatedTemplates();

  return (
    <MainContainer>
      <Container>
        <BackButton onClick={handleBack}>
          <ChevronSvg />
          Back
        </BackButton>
        <Title>Top Creators (Last 7 days)</Title>
        {loading ? (
          <LoadingContainer>
            <l-line-spinner size="32" stroke="2" speed="1" color="#fff" />
          </LoadingContainer>
        ) : topCreators && topCreators.length > 0 ? (
          <>
            <Section>
              {/* Top 3 Podium */}
              <PodiumGrid>
                {podiumOrder.map((rank, index) => {
                  const creator = topCreators?.[rank - 1];
                  return (
                    creator && (
                      <PodiumContainer>
                        <PodiumUsernameWrapper>
                          {rank === 1 ? (
                            <PodiumIcon src={FirstPng} alt="First" />
                          ) : rank === 2 ? (
                            <PodiumIcon src={SecondPng} alt="Second" />
                          ) : (
                            <PodiumIcon src={ThirdPng} alt="Third" />
                          )}

                          <PodiumColumn>
                            <PodiumName>@{creator.username}</PodiumName>
                          </PodiumColumn>
                        </PodiumUsernameWrapper>

                        <PodiumCard
                          key={rank}
                          order={index + 1}
                          $rank={rank}
                        ></PodiumCard>
                      </PodiumContainer>
                    )
                  );
                })}
              </PodiumGrid>

              {/* Rest of Leaderboard */}
              <LeaderboardGrid>
                {topCreators.slice(3).map((creator, index) => (
                  <LeaderboardItem key={index + 3}>
                    <LeaderboardRank>#{index + 4}</LeaderboardRank>
                    <LeaderboardName>@{creator.username}</LeaderboardName>
                  </LeaderboardItem>
                ))}
              </LeaderboardGrid>
            </Section>

            {/* Published Templates Section */}
            {user?.approvedTrainModels &&
            Object.keys(user?.approvedTrainModels).length > 0 ? (
              <TemplatesSection>
                <EmptyStateCard>
                  {paginatedTemplates.map(([id, model], index) => (
                    <Fragment key={id}>
                      <TemplateRow
                        $isLast={index === paginatedTemplates.length - 1}
                      >
                        <TemplateColumn>
                          <TemplateLabel>Template</TemplateLabel>
                          <TemplateValue>{model?.name}</TemplateValue>
                        </TemplateColumn>
                        <TemplateColumn $alignItems="center">
                          <TemplateLabel>First Earned</TemplateLabel>
                          <TemplateValue>
                            {formatTimeAgo(model?.dateApproved)}
                          </TemplateValue>
                        </TemplateColumn>
                        <TemplateColumn $alignItems="flex-end">
                          <TemplateLabel>All</TemplateLabel>
                          <TemplateValue>
                            <StarSvg />
                            {priceFormatter(model?.totalEarned)}
                          </TemplateValue>
                        </TemplateColumn>
                      </TemplateRow>
                      <MobileTemplateRow
                        $isLast={index === paginatedTemplates.length - 1}
                      >
                        <TemplateColumn $gap="8px">
                          <TemplateColumn>
                            <TemplateLabel>Template</TemplateLabel>
                            <TemplateValue>{model?.name}</TemplateValue>
                          </TemplateColumn>
                          <TemplateColumn $alignItems="flex-start">
                            <TemplateLabel>First Earned</TemplateLabel>
                            <TemplateValue>
                              {formatTimeAgo(model?.dateApproved)}
                            </TemplateValue>
                          </TemplateColumn>
                        </TemplateColumn>
                        <TemplateColumn $alignItems="flex-end">
                          <TemplateLabel>All</TemplateLabel>
                          <TemplateValue>
                            <StarSvg />
                            {priceFormatter(model?.totalEarned)}
                          </TemplateValue>
                        </TemplateColumn>
                      </MobileTemplateRow>
                    </Fragment>
                  ))}
                </EmptyStateCard>

                {totalTemplates > itemsPerPage && (
                  <TemplateFooter>
                    <FooterText>
                      Showing {paginatedTemplates.length} of {totalTemplates}{" "}
                      templates
                    </FooterText>
                    <Pagination>
                      <PaginationButton
                        disabled={currentPage <= 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        Previous
                      </PaginationButton>

                      <PaginationCurrent>
                        Page {currentPage} of {totalPages}
                      </PaginationCurrent>

                      <PaginationButton
                        disabled={currentPage >= totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                      </PaginationButton>
                    </Pagination>
                  </TemplateFooter>
                )}
              </TemplatesSection>
            ) : (
              <EmptyStateCard>
                <EmptyStateTitle>No Published Templates</EmptyStateTitle>
                <EmptyStateText>
                  After training, you can publish your template to earn rewards.
                </EmptyStateText>
                <Button
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      left: 0,
                      behavior: "instant",
                    });
                    setTimeout(() => {
                      navigate("/train");
                    }, 200);
                  }}
                  title={"Go to Training"}
                />
              </EmptyStateCard>
            )}
          </>
        ) : (
          // Empty State - No Top Creators
          <EmptyStateCard>
            <EmptyStateIcon>
              <TrophySvg width={48} height={48} color="white" />
            </EmptyStateIcon>
            <EmptyStateTitle>No Top Creators Yet</EmptyStateTitle>
            <EmptyStateText>
              The leaderboard is empty. Be the first to train and publish your
              template to claim the top spot!
            </EmptyStateText>
            <Button
              onClick={() => {
                navigate("/");
                localStorage.setItem("activeTabIndex", "2");
                window.scrollTo(0, 0);
              }}
              title={"Start Training"}
            />
          </EmptyStateCard>
        )}
      </Container>
    </MainContainer>
  );
};

export default RewardsTemplatePage;
